import React from 'react';
import { observer } from 'mobx-react-lite';

import capitalize from '@insurancemenu/imenu-components-lib/dist/helpers/capitalize';
import { Button, Checkbox, colors } from '@insurancemenu/imenu-components-lib';
import { Legal, NoteAdd } from '@insurancemenu/imenu-components-lib/dist/icons';
import { PRODUCT_LINES_CONTENT_MAP } from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { useRootStore } from 'store/rootStore';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import clsx from 'clsx';
import { replaceHtmlBreaks } from '@insurancemenu/imenu-components-lib/dist/helpers/replaceHtmlBreaks';
import { EnrollmentStage, ProductType } from 'types/enrollmentService';
import { toJS } from 'mobx';
import { RenewalAction } from 'types/domainServiceV2';
import { useProductLineDelegate } from 'store/pages/proposal/productLineContext';
import CreatePortfolioModal from 'components/common/proposal/CreatePortfolioModal';
import { createStyles } from './styles';
import useCreatePortfolioModalStore from './useCreatePortfolioModalStore';

interface Props {
  productLineId: ProductLineId;
}

interface ModalProps {
  productLineId: ProductLineId;
}

const UsualCreatePortfolioModal = observer(({ productLineId }: ModalProps) => {
  const store = useCreatePortfolioModalStore();
  return <CreatePortfolioModal productLineId={productLineId} store={store} createPortfolioCombinationType="PROPOSAL" />;
});

const CartItemProductLineHeader = observer(({ productLineId }: Props): JSX.Element => {
  // Mobx store
  const { proposalItemsStore, domainStoreV2 } = useRootStore();
  const { canShowCreatePortfolioModal, createPortfolioModalDelegate } = useProductLineDelegate();

  const productLineName = PRODUCT_LINES_CONTENT_MAP[productLineId].label;
  const proposalProductLine = proposalItemsStore.getProductLineFromProposal(productLineId);
  const installedProductLine = toJS(proposalItemsStore.getInstalledProductLine(productLineId));
  const convertedPortfolioItemsByProductLines = proposalItemsStore.portfoliosByProductLine
    .map((dateObj) => toJS(dateObj))
    .slice();
  const dontProvideIneligible = proposalItemsStore.isDontProvideIneligible(productLineId);

  const portfolioToCartItems =
    convertedPortfolioItemsByProductLines?.find((item) => item.productLineId === productLineId)?.portfolioToCartItems ||
    [];
  const renewalActions =
    domainStoreV2.domainHeader.properties.availableRenewalActions?.map((action) => toJS(action)).slice() || [];

  // Component actions
  function onDontProvideCheckboxChange(): void {
    proposalItemsStore.dontProvideOrCancelCoverage(productLineId);
  }

  // Function calculates is the product line is Renewal and if yes - checks if Action to Cancel coverage is supported by sponsor,
  // If the Product line is a New Sale, always allow "Don't Provide Button"
  function isRenewalCancelCoverageEnable(): boolean {
    if (dontProvideIneligible) {
      return false;
    }
    if (installedProductLine) {
      return renewalActions.includes(RenewalAction.CANCEL_COVERAGE);
    }
    return true;
  }

  // Checks if ProductLine covered in the Installed Proposal of current Enrollment Period
  function shouldHideRenewalProductLine(): boolean {
    return (installedProductLine && proposalItemsStore.enrollmentPeriod.stage === EnrollmentStage.INSTALLED) || false;
  }

  function getProductLineHeader(): JSX.Element {
    switch (proposalProductLine.productType) {
      case ProductType.RENEW: {
        return (
          <>
            <b>Selected: </b> Renew {capitalize(productLineName)} plan as-is
          </>
        );
      }
      case ProductType.CANCEL: {
        return (
          <>
            <b>Selected: </b> Cancel {capitalize(productLineName)} Coverage
          </>
        );
      }
      case ProductType.CHANGE_PLAN: {
        return (
          <>
            <b>Selected: </b> Plan change for {capitalize(productLineName)}
          </>
        );
      }
      case ProductType.NEW_SALE:
      case ProductType.ADD_BENEFIT: {
        if (!proposalProductLine.confirmedPortfolioId) {
          return <>Select one plan for {capitalize(productLineName)} benefits</>;
        }
        const portfolioName = replaceHtmlBreaks(
          proposalItemsStore.getConfirmedPortfolioByProductLine(productLineId)?.name,
        );
        return (
          <>
            <b>Selected Plan: </b> {portfolioName}
          </>
        );
      }
      case ProductType.DONT_PROVIDE: {
        return (
          <>
            <b>Selected: </b> Do not provide {capitalize(productLineName)} Coverage
          </>
        );
      }
      default: {
        return <></>;
      }
    }
  }

  function comparePlansByProductLine(): void {
    proposalItemsStore.compareAllPortfoliosInPl(productLineId);
  }

  function cancelOrDontProvide(): string {
    if (installedProductLine) {
      return ` Cancel ${capitalize(productLineName)} coverage`;
    }
    return ` Do not provide ${capitalize(productLineName)} coverage`;
  }

  const classes = createStyles();

  return (
    <>
      <header className={classes.cartItemsHeader}>
        <div className={classes.titleWrapper}>
          <h3 className={classes.cartItemsTitle}>
            {PRODUCT_LINES_CONTENT_MAP[productLineId].icon('2.4rem')}
            <span className={classes.titleText}>{productLineName}</span>
          </h3>
          {portfolioToCartItems.length > 0 && (
            <Button
              type="text"
              color="primary"
              leftIconProp={<Legal />}
              className={classes.actionButton}
              onClick={comparePlansByProductLine}
            >
              {proposalItemsStore.isRenewalProductLine(productLineId)
                ? `Compare all Alternative ${productLineName} Plans`
                : `Compare all ${productLineName} Plans`}
            </Button>
          )}
        </div>
        <div className={classes.infoWrapper}>
          {shouldHideRenewalProductLine() ? (
            <Checkbox
              checked={proposalItemsStore.isProductLineDontProvide(productLineId)}
              disabled={!proposalItemsStore.isProposalEditable}
              onChange={onDontProvideCheckboxChange}
              highlightColor={
                proposalItemsStore.isProductLineDontProvide(productLineId) ? colors.blue[500] : colors.red[500]
              }
              label={
                <span
                  className={clsx(
                    classes.checkboxLabel,
                    !proposalItemsStore.isProposalEditable && classes.inActiveCheckboxLabel,
                  )}
                >
                  I understand that this group already has {productLineName} coverage
                </span>
              }
            />
          ) : (
            <>
              <p className={classes.cartItemsCaption}>{getProductLineHeader()}</p>
              {isRenewalCancelCoverageEnable() && (
                <Checkbox
                  checked={proposalItemsStore.isProductLineDontProvide(productLineId)}
                  disabled={!proposalItemsStore.isProposalEditable}
                  onChange={onDontProvideCheckboxChange}
                  highlightColor={colors.red[500]}
                  label={
                    <span
                      className={clsx(
                        classes.checkboxLabel,
                        !proposalItemsStore.isProposalEditable && classes.inActiveCheckboxLabel,
                      )}
                    >
                      {cancelOrDontProvide()}
                    </span>
                  }
                />
              )}
            </>
          )}
        </div>
        {!proposalItemsStore.isProductLineDontProvide(productLineId) && canShowCreatePortfolioModal && (
          <div className={classes.createPortfolioSection}>
            <Button
              type="text"
              color="primary"
              leftIconProp={<NoteAdd />}
              onClick={createPortfolioModalDelegate.open}
              className={classes.actionButton}
              disabled={!canShowCreatePortfolioModal()}
            >
              Create Portfolio
            </Button>
          </div>
        )}
      </header>
      <UsualCreatePortfolioModal productLineId={productLineId} />
    </>
  );
});

export default CartItemProductLineHeader;
